import { useState } from 'react';
import { NavBar } from '../../components/NavBar.tsx';
import { Seg } from '../../components/Seg.tsx';
import { ErrorState } from '../../components/DataState.tsx';
import { ApiError } from '../../api/client.ts';
import { saveEntryToBook } from '../../api/rooms.ts';
import { CATEGORIES } from '../../domain.ts';
import type { CategoryName, Entry, PrayerNote } from '../../domain.ts';
import { today } from '../book/shared.ts';

/**
 * F4-2-1 — 내 Prayer Book 으로 저장.
 *
 * 모임에서 적은 기도 제목을 내 기도 노트로 담습니다. **제목과 내용은 이미
 * 줄에 있으므로 받지 않습니다** — 고를 것은 영역과 PLAN 뿐입니다. C2 를 그대로
 * 쓰지 않은 이유이기도 합니다: 이미 적힌 것을 다시 적게 하면, 같은 문장이
 * 두 군데서 갈라질 수 있습니다.
 *
 * 담긴 뒤에는 **이 줄이 그 기도 노트와 이어집니다.** Prayer Book 에서 제목이나
 * 내용을 고치면 모임 기록지의 줄도 함께 바뀝니다 — 대표 기도제목 공유와 같은
 * 방식입니다.
 */

interface Props {
  entry: Entry;
  onCancel: () => void;
  onSaved: (note: PrayerNote) => void;
}

export function SaveToBook({ entry, onCancel, onSaved }: Props) {
  const [category, setCategory] = useState<CategoryName>(CATEGORIES[0]);
  const [plan, setPlan] = useState<'EVERYDAY' | 'DATE'>('EVERYDAY');
  const [date, setDate] = useState(today());
  const [isPrivate, setPrivate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setFailure(null);
    try {
      onSaved(
        await saveEntryToBook(entry.entryId, {
          category,
          plan: plan === 'EVERYDAY' ? { type: 'EVERYDAY' } : { type: 'DATE', date },
          isPrivate,
        }),
      );
    } catch (e) {
      setFailure(e instanceof ApiError ? e.message : '담지 못했습니다.');
      setBusy(false);
    }
  };

  return (
    <div className="screen">
      <NavBar
        title="Prayer Book 으로 저장"
        subtitle={category}
        start={{ icon: 'close', label: '나가기', onClick: onCancel }}
        end={{ icon: 'check', label: '저장', accent: true, onClick: save }}
      />

      <div className="screen__body">
        {failure && <ErrorState message={failure} onRetry={() => setFailure(null)} />}

        {/* 담을 내용은 이미 정해져 있습니다. 고치는 곳이 아니라 보여 주는 곳입니다 */}
        <div className="card card--flat">
          {entry.title !== '' && <p className="card__title">{entry.title}</p>}
          <p className="card__text">{entry.body}</p>
        </div>

        <div className="field">
          <span className="field__label">영역</span>
          <Seg
            label="영역"
            options={CATEGORIES.map((c) => ({ value: c, label: c }))}
            value={category}
            onChange={setCategory}
          />
        </div>

        <div className="field">
          <span className="field__label">Plan</span>
          <Seg
            label="Plan"
            options={[
              { value: 'EVERYDAY', label: 'Everyday' },
              { value: 'DATE', label: '날짜 지정' },
            ]}
            value={plan}
            onChange={setPlan}
          />
          {plan === 'DATE' && (
            <input
              className="input"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          )}
        </div>

        <div className="field">
          <span className="field__label">Private</span>
          <button
            type="button"
            className="toggle-row"
            aria-pressed={isPrivate}
            onClick={() => setPrivate((v) => !v)}
          >
            <span className="toggle-row__label">
              비공개
              <span className="toggle-row__sub">친구에게 공개 노트로 보이지 않습니다</span>
            </span>
            <span className={isPrivate ? 'toggle toggle--on' : 'toggle'} />
          </button>
        </div>

        <p className="hint">
          기도 상태는 <b>기도중</b>, 중요도는 <b>일반</b>으로 담깁니다. 담은 뒤에{' '}
          <b>Prayer Book 에서 고치면 모임 기록지의 줄도 함께 바뀝니다.</b>
        </p>

        <div className="btn-row btn-row--foot">
          <button type="button" className="btn btn--primary" disabled={busy} onClick={save}>
            {busy ? '담는 중…' : 'Prayer Book 에 담기'}
          </button>
        </div>
      </div>
    </div>
  );
}
